// "Retrieval" animations shared by the Archive, Factions and Status panels (Motion).
//
// Things "load in" every time they're opened: rows wipe in left to right, one after
// another, and records reveal their header and then their sections.

import { animate, stagger } from "motion";

const EASE = [0.22, 1, 0.36, 1];

// Drop the finished clip so it can't trim shadows, outlines or expanding content later
const unclip = (controls, els) => {
	controls?.finished?.then(() => setTimeout(() => els.forEach(el => (el.style.clipPath = "")), 50));
	return controls;
};

export const reducedMotion = () =>
	!!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// One clean pass: each row wipes in from the left, staggered. `bleed` widens the reveal
// to the left of each row, so things drawn outside the box (list bullets) come with it.
export function retrieveRows(
	rows,
	{ startDelay = 0, gap = 0.05, duration = 0.42, bleed = 0 } = {}
) {
	const list = [...rows];
	if (!list.length || reducedMotion()) return null;
	return unclip(
		animate(
			list,
			{ opacity: [0, 1], clipPath: [`inset(0 100% 0 -${bleed}px)`, `inset(0 0% 0 -${bleed}px)`] },
			{ duration, delay: stagger(gap, { startDelay }), ease: EASE }
		),
		list
	);
}

// A record opening: the header wipes in, then its blocks rise in turn
export function revealRecord(head, blocks, { startDelay = 0 } = {}) {
	if (reducedMotion()) return;
	if (head)
		unclip(
			animate(
				head,
				{ opacity: [0, 1], clipPath: ["inset(0 100% 0 0)", "inset(0 0% 0 0)"] },
				{ duration: 0.5, delay: startDelay, ease: EASE }
			),
			[head]
		);
	const list = [...blocks].slice(0, 14);
	if (list.length) {
		animate(
			list,
			{ opacity: [0, 1], y: [10, 0] },
			{ duration: 0.45, delay: stagger(0.05, { startDelay: startDelay + 0.18 }), ease: EASE }
		);
	}
}

// The loading bar shown while a record is being "retrieved"
export function runLoadBar(bar, duration) {
	if (!bar) return null;
	return animate(bar, { scaleX: [0, 1] }, { duration, ease: [0.4, 0, 0.2, 1] });
}

// Gauges filling on load: each filled pip/box lights up in turn, from empty to its
// own filled colour, then hands styling back to the stylesheet
export function fillPips(filled, { empty = "rgba(26, 26, 31, 0.5)", delay = 0, gap = 0.08 } = {}) {
	const list = [...filled];
	if (!list.length || reducedMotion()) return;
	list.forEach((el, i) => {
		const style = getComputedStyle(el);
		const bg = style.backgroundColor;
		const border = style.borderColor;
		animate(
			el,
			{ backgroundColor: [empty, bg], borderColor: ["rgba(209, 200, 184, 0.35)", border] },
			{ duration: 0.25, delay: delay + i * gap, ease: "easeOut" }
		).finished.then(() =>
			setTimeout(() => (el.style.backgroundColor = el.style.borderColor = ""), 50)
		);
	});
}

// A number counting up to its value
export function countUp(el, value, { delay = 0, duration = 0.6 } = {}) {
	if (!el || !value || reducedMotion()) return;
	// Write into Vue's own text node, so later re-renders still update the number
	const node = [...el.childNodes].find(n => n.nodeType === Node.TEXT_NODE);
	if (!node) return;
	node.nodeValue = "0";
	animate(0, value, {
		delay,
		duration,
		ease: "easeOut",
		onUpdate: v => (node.nodeValue = String(Math.round(v))),
	});
}
